import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OidcSecurityService, UserDataResult } from 'angular-auth-oidc-client';
import { defaultIfEmpty, map, Observable } from 'rxjs';
import { isAdmin, isInternalRegional } from '../../utils/user-roles';
import { AuthorizationService } from './authorization.service';
import { Permission, Permissions } from './permissions';

@Injectable({
  providedIn: 'root',
})
export class OidcAuthorizationService extends AuthorizationService {
  private readonly securityService = inject(OidcSecurityService);
  private readonly userData$ = this.securityService.userData$;
  private readonly userDataSignal: Signal<UserDataResult | undefined> = toSignal(this.userData$, {
    initialValue: undefined,
  });

  getIsAuthorized$(permission: Permission): Observable<boolean> {
    const requirement = permissionRoleMapping[permission];
    return this.userData$.pipe(
      defaultIfEmpty<UserDataResult, UserDataResult | undefined>(undefined),
      map((user) => this.performCheck(user, requirement))
    );
  }
  getIsAuthorizedSignal(permission: Permission): Signal<boolean> {
    const requirement = permissionRoleMapping[permission];
    return computed(() => this.performCheck(this.userDataSignal(), requirement));
  }

  getIsAuthorized(permission: Permission): boolean {
    return this.getIsAuthorizedSignal(permission)();
  }

  private performCheck(user: UserDataResult | undefined, requirement: RoleRequirement): boolean {
    if (requirement.allowsUnAuthenticated) return true;
    if (requirement.allowsAuthenticated && user != undefined && user != null) return true;
    if (requirement.allowsInternalRegional && isInternalRegional(user)) return true;
    if (requirement.allowsAdmin && isAdmin(user)) return true;
    return false;
  }
}

const permissionRoleMapping = {
  [Permissions.SecurityListUsers]: {
    allowsAdmin: true,
    allowsInternalRegional: false,
    allowsAuthenticated: false,
    allowsUnAuthenticated: false,
  },
  [Permissions.SecurityListGroups]: {
    allowsAdmin: true,
    allowsInternalRegional: false,
    allowsAuthenticated: false,
    allowsUnAuthenticated: false,
  },
  [Permissions.SecurityListAzureUsers]: {
    allowsAdmin: true,
    allowsInternalRegional: false,
    allowsAuthenticated: false,
    allowsUnAuthenticated: false,
  },
  [Permissions.MdmSettingsRead]: {
    allowsAdmin: true,
    allowsInternalRegional: true,
    allowsAuthenticated: false,
    allowsUnAuthenticated: false,
  },
  [Permissions.Admin]: {
    allowsAdmin: true,
    allowsInternalRegional: false,
    allowsAuthenticated: false,
    allowsUnAuthenticated: false,
  },
  [Permissions.InternalRegionalOrAdmin]: {
    allowsAdmin: true,
    allowsInternalRegional: true,
    allowsAuthenticated: false,
    allowsUnAuthenticated: false,
  },
  [Permissions.Authenticated]: {
    allowsAdmin: true,
    allowsInternalRegional: true,
    allowsAuthenticated: true,
    allowsUnAuthenticated: false,
  },
  [Permissions.UnAuthenticated]: {
    allowsAdmin: true,
    allowsInternalRegional: true,
    allowsAuthenticated: true,
    allowsUnAuthenticated: true,
  },
} satisfies Record<Permission, RoleRequirement>;

type RoleRequirement = {
  allowsAdmin: boolean;
  allowsInternalRegional: boolean;
  allowsAuthenticated: boolean;
  allowsUnAuthenticated: boolean;
};
